/**
 * HistoryManager — stack-based undo/redo over full app-state snapshots.
 *
 * Strategy:
 *
 *   - A snapshot is whatever `capture()` returns. Conceptually a deep
 *     enough copy that `apply(snapshot)` restores the user's state.
 *     In practice we hand it `presetManager.captureState()` and
 *     `presetManager.applyState({...})`, so the same code path that
 *     round-trips presets does the heavy lifting.
 *
 *   - Two stacks: `undoStack` holds previous states (oldest at
 *     index 0); `redoStack` holds states the user has just undone
 *     (the most-recently-undone at the top). New work clears redo.
 *
 *   - Two push entry points:
 *
 *       `push()` — discrete actions (randomize, preset load, mod
 *         matrix add/remove). Captures BEFORE the action so undoing
 *         lands you back at the pre-action state.
 *
 *       `scheduleCoalesced()` — burst-y actions (knob drag, slider).
 *         The first call in a burst captures a "pre" snapshot and
 *         starts a debounce timer; subsequent calls reset the timer.
 *         When the timer fires, the pre-snapshot is pushed and the
 *         burst counts as one undo step.
 *
 *   - Snapshot equality is shallow-by-id: we compare with the most
 *     recent stack entry by JSON serialization and skip the push
 *     when nothing actually changed. Cheap insurance against
 *     redundant entries (e.g. apply() round-trips a value back to
 *     itself, or a "ui" listener fires with no real delta).
 *
 *   - `applyInProgress` flag suppresses listeners during undo/redo —
 *     applying a snapshot fires every Parameter's subscribe with
 *     source `'preset'`, so we don't want THAT to schedule a fresh
 *     coalesced push.
 *
 *   - The stacks are bounded (max 50 by default). Hitting the cap
 *     drops the oldest entry; users get the most recent edits at
 *     the cost of forgetting deep history.
 */

export interface HistoryManagerOptions<S> {
  /** Snapshot of current state. Called whenever the manager wants
   *  to record a checkpoint. */
  capture: () => S;
  /** Restore a snapshot — typically `presetManager.applyState(snapshot)`. */
  apply: (snapshot: S) => void;
  /** Maximum stack depth. Default 50. */
  maxSize?: number;
  /** Debounce window for coalesced pushes, in ms. Default 500. */
  coalesceMs?: number;
}

/** Listener fires whenever the available undo/redo state changes. */
export type HistoryListener = (state: {
  canUndo: boolean;
  canRedo: boolean;
}) => void;

export class HistoryManager<S> {
  private readonly captureFn: () => S;
  private readonly applyFn: (snapshot: S) => void;
  private readonly maxSize: number;
  private readonly coalesceMs: number;

  private undoStack: S[] = [];
  private redoStack: S[] = [];
  /** During a coalesced burst this holds the pre-burst capture. */
  private pendingPre: S | null = null;
  /**
   * Snapshot of the most recent "settled" state — refreshed after
   * every commit / push / undo / redo. Used as the pre-snapshot
   * when a coalesced burst starts, since `scheduleCoalesced()`
   * typically fires AFTER a Parameter has changed (the change
   * propagates through subscribe listeners) and we want undo to
   * land on the value the user had BEFORE the burst.
   */
  private lastSnapshot: S;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** True while we're inside `apply()` — used to guard against the
   *  resulting Parameter notifications (which would otherwise
   *  schedule a fresh coalesced push). */
  private applyInProgress = false;

  private readonly listeners = new Set<HistoryListener>();

  constructor(opts: HistoryManagerOptions<S>) {
    this.captureFn = opts.capture;
    this.applyFn = opts.apply;
    this.maxSize = opts.maxSize ?? 50;
    this.coalesceMs = opts.coalesceMs ?? 500;
    // Seed the baseline immediately so the first coalesced burst
    // captures the pre-burst state correctly.
    this.lastSnapshot = this.captureFn();
  }

  /**
   * Re-capture the baseline snapshot. Call this when the app
   * finishes a phase of construction (e.g. after `buildAudioGraph`
   * lands a fresh voice manager) so the next user interaction's
   * pre-burst snapshot reflects the current ground state.
   */
  reseed(): void {
    this.lastSnapshot = this.captureFn();
  }

  /**
   * Discrete push — captures NOW and pushes onto the undo stack.
   * Used by callers that know they're about to do something
   * undoable (randomize, preset load). The captured state is the
   * pre-action state, so `undo()` returns the user there.
   */
  push(): void {
    // Commit any in-flight coalesced burst first so pre-action and
    // post-burst snapshots don't collide.
    this.commit();
    const snap = this.captureFn();
    this.recordSnapshot(snap);
    this.lastSnapshot = snap;
  }

  /**
   * Burst-friendly push — call this every time a parameter changes
   * during a drag. The first call in a burst captures the pre-drag
   * state; subsequent calls bump the debounce timer. When the timer
   * settles (no calls for `coalesceMs`), the captured pre-state is
   * pushed and the burst counts as one undo step.
   */
  scheduleCoalesced(): void {
    if (this.applyInProgress) return;
    if (this.pendingPre === null) {
      // Use the previously-settled baseline as the pre-burst
      // snapshot — `scheduleCoalesced()` is typically called AFTER
      // a Parameter listener fires (i.e. AFTER the value already
      // changed), so capturing now would record the new value, not
      // the old. The lastSnapshot reflects what state was right
      // before this burst began.
      this.pendingPre = this.lastSnapshot;
    }
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.commit(), this.coalesceMs);
  }

  /**
   * Force any in-flight coalesced burst to land immediately.
   * Idempotent — if there's no burst, no-op. Used internally before
   * undo/redo so the stacks reflect everything the user has done.
   */
  commit(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    const pre = this.pendingPre;
    this.pendingPre = null;
    if (pre === null) return;
    this.recordSnapshot(pre);
    // Refresh the baseline so the NEXT burst captures the current
    // (post-burst) state as its pre-snapshot.
    this.lastSnapshot = this.captureFn();
  }

  canUndo(): boolean {
    return this.undoStack.length > 0 || this.pendingPre !== null;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Step back one snapshot. Pushes the current state onto redoStack
   * so redo() can return to it. No-op when the undo stack is empty
   * AND there's no pending burst.
   */
  undo(): void {
    this.commit();
    const prev = this.undoStack.pop();
    if (prev === undefined) return;
    const current = this.captureFn();
    this.applyInProgress = true;
    try {
      this.applyFn(prev);
    } finally {
      this.applyInProgress = false;
    }
    this.redoStack.push(current);
    this.lastSnapshot = this.captureFn();
    this.emit();
  }

  /**
   * Replay one redo step. No-op when redoStack is empty. Restores
   * the most-recently-undone state and pushes the now-current state
   * back onto undoStack.
   */
  redo(): void {
    if (this.redoStack.length === 0) return;
    const next = this.redoStack.pop()!;
    const current = this.captureFn();
    this.applyInProgress = true;
    try {
      this.applyFn(next);
    } finally {
      this.applyInProgress = false;
    }
    this.undoStack.push(current);
    this.lastSnapshot = this.captureFn();
    this.emit();
  }

  /** Drop both stacks. Useful on session reset / sign-out. */
  clear(): void {
    this.commit();
    this.undoStack = [];
    this.redoStack = [];
    this.pendingPre = null;
    this.emit();
  }

  /** Subscribe to canUndo/canRedo transitions. Fires immediately
   *  with the current state so callers can render an accurate
   *  Undo/Redo button affordance from the moment they subscribe. */
  subscribe(fn: HistoryListener): () => void {
    this.listeners.add(fn);
    fn({ canUndo: this.canUndo(), canRedo: this.canRedo() });
    return () => {
      this.listeners.delete(fn);
    };
  }

  // ── Internals ─────────────────────────────────────────────────────────

  /** True while undo/redo is applying a snapshot — callers should use
   *  this to suppress their own coalesced pushes. */
  isApplying(): boolean {
    return this.applyInProgress;
  }

  private recordSnapshot(snap: S): void {
    // Skip duplicate entries — e.g. when apply() round-trips a value
    // unchanged or a UI listener fires with no real delta.
    const top = this.undoStack[this.undoStack.length - 1];
    if (top !== undefined && deepEqual(top, snap)) return;
    this.undoStack.push(snap);
    while (this.undoStack.length > this.maxSize) this.undoStack.shift();
    // Any new work invalidates the redo stack — once you fork the
    // history past an undo, the previously-undone redo path is lost.
    this.redoStack = [];
    this.emit();
  }

  private emit(): void {
    const state = { canUndo: this.canUndo(), canRedo: this.canRedo() };
    for (const fn of this.listeners) fn(state);
  }
}

/**
 * JSON-equality check. Snapshot types are plain data (numbers,
 * strings, arrays, plain objects) so this is sufficient — and avoids
 * pulling in a deep-equal dependency for one call site.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}
